import { Box, Button, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { getFleetMileageHistory, recordFleetMileage } from "../../../../../services/dashboard";
import { useAuthStore } from "../../../../../store/useAuthStore";
import { useVehicleStore } from "../../../../../store/useVehicleStore";
import { useToast } from "../../../../../hooks/useToast";
import {
  OperationsEmptyState,
  OperationsSectionHeader,
  operationsTableHeadSx,
  operationsTablePaperSx,
  operationsRowSx,
} from "../../../ifas/components/OperationsUI";

const toDateTimeLocalValue = (date = new Date()) => {
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 16);
};

const toApiDateTime = (value: string) => (value ? `${value}:00` : "");

const formatDateTime = (value?: string) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

export default function Mileage() {
  const { selectedVehicle, setSelectedVehicle } = useVehicleStore();
  const { first_name, last_name, user_name } = useAuthStore();
  const { showInfoToast } = useToast();
  const [records, setRecords] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    mileageOut: selectedVehicle?.currentMileage != null ? String(selectedVehicle.currentMileage) : "",
    mileageIn: "",
    movementStartedAt: toDateTimeLocalValue(),
    movementEndedAt: "",
    notes: "",
  });

  const recordedBy = useMemo(
    () =>
      [first_name, last_name].filter(Boolean).join(" ") ||
      user_name ||
      "Fleet User",
    [first_name, last_name, user_name],
  );

  const loadHistory = async () => {
    if (!selectedVehicle?.vehicleID) return;
    const response = await getFleetMileageHistory(selectedVehicle.vehicleID);
    if (response?.status === 200 && Array.isArray(response.data)) {
      setRecords(response.data);
    }
  };

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      mileageOut:
        selectedVehicle?.currentMileage != null
          ? String(selectedVehicle.currentMileage)
          : prev.mileageOut,
    }));
    void loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVehicle?.vehicleID]);

  const updateField =
    (field: keyof typeof formData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const validate = () => {
    const mileageOut = Number(formData.mileageOut);
    const mileageIn = formData.mileageIn === "" ? null : Number(formData.mileageIn);

    if (!selectedVehicle?.vehicleID) {
      showInfoToast("Unable to identify the selected vehicle");
      return false;
    }
    if (!Number.isFinite(mileageOut) || mileageOut < 0) {
      showInfoToast("Mileage out must be a valid positive number");
      return false;
    }
    if (selectedVehicle.currentMileage != null && mileageOut < selectedVehicle.currentMileage) {
      showInfoToast("Mileage out cannot be less than current mileage");
      return false;
    }
    if (mileageIn !== null && (!Number.isFinite(mileageIn) || mileageIn < mileageOut)) {
      showInfoToast("Mileage in cannot be less than mileage out");
      return false;
    }
    if (!formData.movementStartedAt) {
      showInfoToast("Movement start date/time is required");
      return false;
    }
    if (formData.movementEndedAt && formData.movementEndedAt < formData.movementStartedAt) {
      showInfoToast("Movement end cannot be before movement start");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate() || !selectedVehicle?.vehicleID) return;

    setIsSaving(true);
    try {
      const payload = {
        mileageOut: Number(formData.mileageOut),
        mileageIn: formData.mileageIn === "" ? undefined : Number(formData.mileageIn),
        movementStartedAt: toApiDateTime(formData.movementStartedAt),
        movementEndedAt: formData.movementEndedAt ? toApiDateTime(formData.movementEndedAt) : undefined,
        source: "MANUAL",
        recordedBy,
        notes: formData.notes || undefined,
      };
      const response = await recordFleetMileage(selectedVehicle.vehicleID, payload);
      if (response?.status === 200 || response?.status === 201) {
        const latestMileage = response.data?.mileageIn ?? response.data?.mileageOut;
        setSelectedVehicle({
          ...selectedVehicle,
          currentMileage: latestMileage ?? selectedVehicle.currentMileage,
        });
        setFormData({
          mileageOut: latestMileage != null ? String(latestMileage) : "",
          mileageIn: "",
          movementStartedAt: toDateTimeLocalValue(),
          movementEndedAt: "",
          notes: "",
        });
        await loadHistory();
      } else {
        showInfoToast(response?.data?.message || "Unable to record mileage");
      }
    } catch (error) {
      showInfoToast("Unable to record mileage");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box>
      <OperationsSectionHeader
        title="Mileage Control"
        meta={
          selectedVehicle?.currentMileage != null
            ? `Current mileage: ${selectedVehicle.currentMileage} km`
            : "Current mileage not captured"
        }
      />

      <Paper elevation={0} sx={{ p: 2, mb: 2, border: "1px solid #EAECF0", borderRadius: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Mileage Out"
              value={formData.mileageOut}
              onChange={updateField("mileageOut")}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Mileage In"
              value={formData.mileageIn}
              onChange={updateField("mileageIn")}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              type="datetime-local"
              label="Movement Started"
              value={formData.movementStartedAt}
              onChange={updateField("movementStartedAt")}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              type="datetime-local"
              label="Movement Ended"
              value={formData.movementEndedAt}
              onChange={updateField("movementEndedAt")}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              label="Notes"
              value={formData.notes}
              onChange={updateField("notes")}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Box display="flex" justifyContent="flex-end">
              <Button variant="contained" color="secondary" onClick={handleSave} disabled={isSaving}>
                {isSaving ? "Saving..." : "Record Mileage"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <OperationsSectionHeader title="Mileage History" meta={`${records.length} records`} />
      <Paper sx={operationsTablePaperSx} elevation={0}>
        {records.length === 0 ? (
          <OperationsEmptyState title="No mileage records yet" />
        ) : (
          <TableContainer sx={{ maxHeight: 360 }}>
            <Table>
              <TableHead sx={operationsTableHeadSx}>
                <TableRow>
                  <TableCell>Mileage Out</TableCell>
                  <TableCell>Mileage In</TableCell>
                  <TableCell>Distance</TableCell>
                  <TableCell>Started</TableCell>
                  <TableCell>Ended</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Recorded By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.map((record) => (
                  <TableRow key={record.id || `${record.movementStartedAt}-${record.mileageOut}`} sx={operationsRowSx}>
                    <TableCell>{record.mileageOut} km</TableCell>
                    <TableCell>{record.mileageIn != null ? `${record.mileageIn} km` : "Open"}</TableCell>
                    <TableCell>
                      {record.distanceTravelled != null ? `${record.distanceTravelled} km` : "N/A"}
                    </TableCell>
                    <TableCell>{formatDateTime(record.movementStartedAt)}</TableCell>
                    <TableCell>{formatDateTime(record.movementEndedAt)}</TableCell>
                    <TableCell>{record.source || "MANUAL"}</TableCell>
                    <TableCell>{record.recordedBy || "N/A"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
